'use client';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import { signOut, useSession } from 'next-auth/react';
import { LogOut } from 'lucide-react';
import { Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User2 } from 'lucide-react';
import { ChevronUp } from 'lucide-react';
import { CircleUserRound } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { LogoImage, LogoRegister, LogoSlogan, LogoText } from '@/components/common';
import { Home } from 'lucide-react';
import { LogIn } from 'lucide-react';
import QuickGuideDialogue from '@/components/QuickGuide';

export default function GuestLayout({ children }) {
  const router = useRouter();
  const { data: session, status, update } = useSession();
  const { setOpenMobile, isMobile, open } = useSidebar();

  const [openSettingModal, setOpenSettingModal] = useState(false);

  const handleOptionSelect = (link, url) => {
    if (link.startsWith('https')) {
      // External link, open in new tab
      window.open(link, '_blank');
    } else {
      // Internal link
      router.push(link);
    }
    closeSideBarInMobile();
  };

  const closeSideBarInMobile = () => {
    if (isMobile) {
      setOpenMobile(false); // close mobile sidebar
    }
  };

  const loggedInMemberLinks = [
    {
      id: 'journey',
      label: 'Continue My Guided Journey',
      href: session?.user?.lastActivePath || '/chat',
    },
    {
      id: 'assessments',
      label: 'View My Assessments and Reports',
      href: '/assessment/reports',
    },
    { id: 'videos', label: 'Watch Growth Videos', href: '/chat' },
    { id: 'blogs', label: 'Access the Blogs', href: '/blogs' },
  ];

  const privateMemberLinks = [
    { id: 'home', label: 'Watch Videos', href: '/videos' },
    {
      id: 'assessments',
      label: 'Browse Blogs',
      href: '/blogs',
    },
  ];

  return (
    <>
      <Sidebar className={cn('bg-white')}>
        <SidebarHeader className={cn('bg-white pt-0')}>
          <div className="flex items-center justify-between py-4">
            <div className={`flex flex-col items-center justify-center`}>
              <Link href="/" className={`flex flex-col items-center justify-start`}>
                <LogoImage className={'h-10 w-40'} />
                <LogoSlogan className="me-4 text-center text-[.7rem]" />
              </Link>
            </div>

            <SidebarTrigger className="me-3 cursor-e-resize" />
          </div>
        </SidebarHeader>

        <SidebarContent className={cn('bg-white')}>
          <SidebarGroup className={`p-0`}>
            <SidebarGroupContent>
              <SidebarMenu>
                {status === 'authenticated' ? (
                  <>
                    {loggedInMemberLinks.map((item, idx) => (
                      <SidebarMenuItem className="cursor-pointer" key={idx}>
                        <SidebarMenuButton asChild>
                          <Link href={`${item.href}`} className="flex items-center gap-2">
                            {item.label}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </>
                ) : (
                  <>
                    {privateMemberLinks.map((item, idx) => (
                      <SidebarMenuItem className="cursor-pointer" key={idx}>
                        <SidebarMenuButton asChild>
                          <Link href={`${item.href}`} className="flex items-center gap-2">
                            {item.label}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </>
                )}

                <SidebarMenuItem className="flex cursor-pointer px-2">
                  <SidebarMenuButton asChild>
                    <QuickGuideDialogue />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              {status === 'authenticated' ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton className={`flex cursor-pointer items-center capitalize`}>
                      <User2 />
                      <span className="mt-1 text-sm font-semibold">{session?.user?.firstName}</span>
                      <ChevronUp className="ml-auto" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="top"
                    className="w-[--radix-popper-anchor-width] min-w-20"
                  >
                    <DropdownMenuItem className={`dis`}>
                      <CircleUserRound className="me-1" />
                      <span className={`block`}>{session?.user?.email}</span>
                    </DropdownMenuItem>

                    <hr className="" />

                    <DropdownMenuItem onClick={() => signOut()} className={`cursor-pointer`}>
                      <LogOut className="mr-1 h-4 w-4 text-red-500" />
                      <span className={`text-red-500 hover:text-red-700`}>Sign out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <SidebarMenuButton asChild>
                  <Link
                    href="/signin"
                    className="group hover:bg-accent flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-all duration-200"
                  >
                    <span className="text-foreground text-sm font-medium">Login</span>
                    <LogIn className="text-muted-foreground group-hover:text-foreground h-4 w-4 flex-shrink-0 transition-colors" />
                  </Link>
                </SidebarMenuButton>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <main className="relative w-full bg-white">
        {open === false && (
          <div className="fixed top-4 left-4 z-20">
            <SidebarTrigger className="me-3 cursor-e-resize" />
          </div>
        )}

        <div className="p-5">{children}</div>
      </main>
    </>
  );
}
